import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import type {
  CreateActivityRequest,
  UpdateActivityRequest,
  ActivityResponse
} from 'generated';
import { useGetQualifications } from 'queries';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, Grid, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { ControlledTextField, Dialog, MultiSelect } from 'components/shared';

import addActivitySchema from './add-activity-schema';

const schema = addActivitySchema();
type Form = InferType<typeof schema>;

const defaultValues = {
  name: '',
  code: '',
  description: ''
};

type Props = {
  open: boolean;
  handleClose: () => void;
  activity?: ActivityResponse;
};

export default function AddActivityDialog({
  open,
  handleClose,
  activity
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: createActivity, isLoading } = mutations.useCreateActivity();
  const { mutate: updateActivity } = mutations.useUpdateActivity();
  const { data: allQualifications, isLoading: isLoadingQualifications } =
    useGetQualifications({ enabled: open });

  const { control, handleSubmit, reset } = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (!activity) {
      reset(defaultValues);
      return;
    }

    reset({
      name: activity.name,
      code: activity.code,
      description: activity.description,
      qualifications: activity.requiredQualifications
    });
  }, [activity, open]);

  async function onSubmit(data) {
    const { name, code, description, qualifications } = data;
    const requiredQualificationEntityIds =
      qualifications?.map((q) => q.id) ?? [];
    if (activity) {
      const payload: UpdateActivityRequest = {
        entityId: activity.entityId,
        name,
        code,
        description,
        requiredQualificationEntityIds: requiredQualificationEntityIds,
        schedulingLabel: 'WORKPLACE'
      };

      await updateActivity(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getActivities.name);
          queryClient.invalidateQueries('planning');
          handleClose();
        }
      });

      return;
    }

    const payload: CreateActivityRequest = {
      name,
      code,
      description,
      requiredQualificationEntityIds: requiredQualificationEntityIds,
      schedulingLabel: 'WORKPLACE'
    };

    createActivity(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getActivities.name);
        handleClose();
      }
    });
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        title={
          activity
            ? t('activityPlanning.editActivity')
            : t('activityPlanning.addActivity')
        }
        open={open}
        handleClose={handleClose}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <ControlledTextField
                name="name"
                control={control}
                label={t('name')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledTextField
                name="code"
                control={control}
                label={t('code')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                name="description"
                control={control}
                label={t('description')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="qualifications"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    onChange={(options) => {
                      field.onChange(options);
                    }}
                    options={allQualifications ?? []}
                    optionLabelProp="name"
                    selectedOptions={field.value}
                    renderTags={(e) =>
                      `${e.length} ${t('selected').toLowerCase()}`
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('qualifications')}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    loading={isLoadingQualifications}
                  />
                )}
              />
            </Grid>
          </Grid>
          <DialogActions sx={{ mt: 3 }}>
            <Button
              type="button"
              variant="text"
              color="dark"
              onClick={() => {
                handleClose();
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              color="dark"
            >
              {t('save')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
}
